import { createContext, useContext, useRef } from "react";
import axios from "axios";
import { useLoad } from "../../components/Load/useLoad";
import { FLAGGED_MODULES } from "./modules";
import MODULES_WITH_UNSAID_LOAD_SITES from "../../guards/modules-with-unsaid-load-sites.json";

/**
 * Which modules are switched on here - GET /api/features, asked once where it is needed (the signed-in app, and each
 * public page of a flagged module). Public and booleans only (owner, 15 Oct 2026).
 *
 * Until the first answer, nothing under the provider is shown (`fallback`): a screen never starts as if a module were
 * on and then vanishes. If the question fails, every flagged module is treated as off - and that is said, never
 * passed off as "switched off" (FeaturesLoadBanner, ModuleGate). Asking again keeps the page as it is meanwhile, so a
 * form being filled in is not lost.
 */
const FeaturesContext = createContext(null);

export function FeaturesProvider({ children, fallback = null }) {
  const load = useLoad(() => axios.get(`${process.env.REACT_APP_API_URL}/cmms/api/features`).then((r) => r.data), []);
  const settled = useRef(null);
  if (load.status === "succeeded" || load.status === "failed") settled.current = load;
  if (!settled.current) return fallback;
  return (
    <FeaturesContext.Provider value={{ ...settled.current, reload: load.reload }}>
      {children}
    </FeaturesContext.Provider>
  );
}

/**
 * Whether this build holds back a module the server has on while some of its load sites still don't say a failed load
 * (owner, 15 and 16 Oct 2026). Such a module isn't switched on - the deploy refuses it - but a module switched on by hand
 * at the server never passes through a deploy, and only the UI knows its own load sites: so a deployed build holds the
 * module's screens back itself, and says so (ModuleGate, FeaturesHeldBackBanner). Not in development or tests, nor on
 * the local stacks, whose compose files build with REACT_APP_LOCAL_STACK=true: every module is on there by design, its
 * work unfinished. Which modules: src/guards/modules-with-unsaid-load-sites.json, held to the guard's list by a test.
 */
export function holdsBackModulesWithUnsaidLoadSites() {
  return process.env.NODE_ENV === "production" && process.env.REACT_APP_LOCAL_STACK !== "true";
}

/**
 * { status: "succeeded" | "failed", load, isOn(module), isHeldBack(module), heldBack }. A module is on only when the
 * server said so and this build doesn't hold it back. Outside a provider - a screen rendered on its own, as in its
 * tests - every module is on, as in development.
 */
export function useFeatures() {
  const load = useContext(FeaturesContext);
  if (!load) return { status: "succeeded", load: null, isOn: () => true, isHeldBack: () => false, heldBack: [] };
  const serverHasOn = (module) => load.status === "succeeded" && load.data?.[module] === true;
  const isHeldBack = (module) => serverHasOn(module) && holdsBackModulesWithUnsaidLoadSites()
    && MODULES_WITH_UNSAID_LOAD_SITES.includes(module);
  return {
    status: load.status,
    load,
    isOn: (module) => !module || (serverHasOn(module) && !isHeldBack(module)),
    isHeldBack,
    heldBack: FLAGGED_MODULES.filter(isHeldBack),
  };
}
